import { Children, cloneElement, isValidElement } from 'react';
import type { ComponentProps, ReactNode } from 'react';

import { FormLabel } from '@playbook/atoms/form-label';
import type { CheckboxSize } from '@playbook/atoms/inputs/checkbox';

import css from './styles.module.css';

const DefaultCheckboxGroupSize: CheckboxSize = 'md' as const;

type CheckboxGroupEventHandlers = {
	[K in keyof ComponentProps<'div'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'div'>[K];
};

interface CheckboxGroupDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface CheckboxGroupProperties
	extends CheckboxGroupEventHandlers, CheckboxGroupDataAttributes {
	size?: CheckboxSize;
	optional?: boolean;
	required?: boolean;
	/** Keeps the label for assistive technology but draws no header. */
	hideLabel?: boolean;

	/** The `name` the options share, and the base of the header's ids. */
	name: string;
	label: string;
	description?: string;
	help?: string;

	/** `Checkbox`es, each with a `label`. */
	children: ReactNode;
}

/**
 *	A header over a column of checkboxes. The group writes `size` onto every
 *	option, the way `InputGroup` does, because options of mixed sizes read as
 *	separate questions.
 *
 *	The header is `atoms/form-label`, whose `<label>` has no single field to
 *	point at here, so the group names itself with `aria-labelledby` instead.
 */
export function CheckboxGroup({
	size = DefaultCheckboxGroupSize,
	optional = false,
	required = false,
	hideLabel = false,

	name,
	label,
	description,
	help,

	children,

	...props
}: CheckboxGroupProperties): ReactNode {
	const options: ReactNode = Children.map(children, (child) =>
		isValidElement<{ size?: CheckboxSize }>(child)
			? cloneElement(child, { size })
			: child
	);

	return (
		<div
			{...props}
			role="group"
			aria-label={hideLabel ? label : undefined}
			aria-labelledby={hideLabel ? undefined : `${name}-label`}
			aria-describedby={
				!hideLabel && description ? `${name}-description` : undefined
			}
			data-size={size}
			className={css.group}>
			{!hideLabel && (
				<FormLabel
					size={size}
					optional={optional}
					required={required}
					htmlFor={name}
					label={label}
					description={description}
					help={help}
				/>
			)}
			<div className={css.options}>{options}</div>
		</div>
	);
}
